import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Typography } from "@/components/ui/typography";

export type ToastVariant = "default" | "destructive" | "success";

export interface ToastProps {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  action?: React.ReactNode;
  duration?: number;
  onClose?: (id: string) => void;
}

function Toast({
  id,
  title,
  description,
  variant = "default",
  action,
  duration = 5000,
  onClose,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show toast after mount
    const showTimer = setTimeout(() => setIsVisible(true), 100);

    // Auto hide after duration
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose?.(id), 300); // Wait for animation
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [id, duration, onClose]);

  const isDestructive = variant === "destructive";
  const isSuccess = variant === "success";

  return (
    <div
      className={`
        fixed z-[70] border transform transition-all duration-300 ease-in-out
        ${
          isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        }
        ${
          isDestructive
            ? "bg-destructive border-destructive text-white"
            : isSuccess
            ? "bg-success-toast border-success-toast text-white"
            : "bg-background border-border text-foreground"
        }
        
        // Mobile positioning with 24px margins - responsive width and height
        bottom-6 left-6 right-6 min-h-[80px]
        
        // Desktop positioning (bottom-right) with responsive dimensions
        lg:bottom-4 lg:right-4 lg:left-auto lg:w-[388px] lg:min-w-[388px] lg:max-w-[420px] lg:min-h-[96px]
        
        // Responsive shadow
        shadow-lg
        
        // Border radius md
        rounded-md pointer-events-auto
      `}
      style={{
        padding: "16px", // 16px all around
        opacity: 1,
        wordWrap: "break-word", // Ensure long words wrap
        overflowWrap: "break-word", // Better text wrapping
      }}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="flex items-start gap-3 min-h-0">
        {/* Content */}
        <div className="flex-1 space-y-2 min-w-0">
          {title && (
            <div>
              <Typography
                variant="small"
                className="font-semibold block leading-tight"
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  lineHeight: "1.4em",
                  display: "block",
                  wordBreak: "break-word",
                }}
              >
                {title}
              </Typography>
            </div>
          )}
          {description && (
            <div>
              <Typography
                variant="small"
                className={`block leading-tight ${
                  isDestructive
                    ? "text-white"
                    : isSuccess
                    ? "text-white"
                    : "inherit"
                }`}
                style={{
                  fontSize: "14px",
                  lineHeight: "1.4em",
                  display: "block",
                  wordBreak: "break-word",
                }}
              >
                {description}
              </Typography>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(() => onClose?.(id), 300);
          }}
          className={`
            flex-shrink-0 p-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity
            ${
              isSuccess
                ? "text-white hover:bg-white hover:text-white"
                : isDestructive
                ? "text-white hover:bg-white hover:text-destructive"
                : "text-muted-foreground hover:text-foreground"
            }
          `}
          style={{
            padding: "4px",
          }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Action button at bottom if provided */}
      {action && (
        <div className="mt-3 flex justify-end">
          <button
            className={`
              px-3 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer
              ${
                isSuccess
                  ? "hover:bg-success-toast/90 text-white"
                  : isDestructive
                  ? "hover:bg-white/10 text-white"
                  : "hover:bg-accent text-accent-foreground"
              }
            `}
          >
            {action}
          </button>
        </div>
      )}
    </div>
  );
}

export { Toast };
